import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Table } from 'primeng/table';
import { Menu } from 'primeng/menu';
import { MenuItem, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

import { BaseServices } from '../../kuba.services';
import { SdsApiService, SdsReplacementCandidate } from '../services/sds-api.service';
import { KubaExportService } from '../../shared/kuba-exports/kuba-export.service';
import {
  SdsExportColumn,
  SdsExportFormat,
  SdsExportService
} from '../services/sds-export.service';
import { SdsSubstitutionAssessmentRow } from '../models/sds.model';

interface StatusOption {
  labelKey: string;
  value: string;
}

/**
 * Yearly substitution-assessment modal: lists chemicals with their last-assessed date,
 * lets the user assess one or many at once (setting a reminder), mark a product as
 * replaced (archive), filter by status, and export the list to Excel/PDF for authorities.
 */
@Component({
  selector: 'app-sds-substitution-assessment',
  templateUrl: './sds-substitution-assessment.component.html',
  styleUrls: ['./sds-substitution-assessment.component.css', './sds-theme.css'],
  providers: [ KubaExportService, SdsExportService],
  standalone: false
})
export class SdsSubstitutionAssessmentComponent implements OnInit, OnDestroy {
  /** Resolved from the active business — this is now a routed page, not a modal. */
  businessId = BaseServices.BusinessId;

  @ViewChild('assessTable') assessTable: Table | null = null;

  rows: SdsSubstitutionAssessmentRow[] = [];
  selected: SdsSubstitutionAssessmentRow[] = [];
  loading = false;

  /** Document ids assessed in this session — shown on top immediately after save. */
  recentlyAssessed = new Set<number>();

  statusFilter = 'active';
  statusOptions: StatusOption[] = [
    { labelKey: 'SDS_SUBST_STATUS_ACTIVE', value: 'active' },
    { labelKey: 'SDS_SUBST_STATUS_ARCHIVED', value: 'archived' },
    { labelKey: 'SDS_SUBST_STATUS_ALL', value: 'all' }
  ];

  // ----- Note info popup -----
  notePopupVisible = false;
  notePopupText = '';
  notePopupProduct = '';

  // ----- Assess popup -----
  assessDialogVisible = false;
  assessDialogSaving = false;
  assessmentDate: Date = new Date();
  reminderDate: Date | null = this.defaultReminder();
  assessNote = '';
  private pendingDocIds: number[] = [];

  // ----- Replace modal (choose: existing vs new) -----
  replaceModalVisible = false;
  replaceStep: 'choice' | 'existing' = 'choice';
  replaceTarget: SdsSubstitutionAssessmentRow | null = null;
  replaceCandidateSuggestions: SdsReplacementCandidate[] = [];
  selectedReplacementCandidate: SdsReplacementCandidate | null = null;
  replaceReason = '';
  replaceSaving = false;
  replaceError: string | null = null;
  private replacementSearchRequestId = 0;

  // ----- Export (same control and formats as the Exposure register) -----
  exportLoading = false;
  exportMenuItems: MenuItem[] = [];
  @ViewChild('exportMenu', { static: false }) exportMenu?: Menu;

  // ----- PDF preview side-panel (mirror of the chemicals list) -----
  previewVisible = false;
  previewLoading = false;
  previewProduct = '';
  previewUrl: SafeResourceUrl | null = null;
  private previewBlobUrl: string | null = null;

  constructor(
    private readonly sdsApi: SdsApiService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly sdsExport: SdsExportService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnDestroy(): void {
    this.revokePreviewBlob();
  }

  /** Open the SDS PDF for a product in a right-hand preview side-panel. */
  async openProductPreview(row: SdsSubstitutionAssessmentRow): Promise<void> {
    this.revokePreviewBlob();
    this.previewProduct = row.Product;
    this.previewUrl = null;
    this.previewVisible = true;
    this.previewLoading = true;

    try {
      const file = await this.sdsApi
        .getDocumentFile(row.DocumentId, null, `${row.Product || 'SDS'}.pdf`, 'application/pdf')
        .toPromise();

      if (!file) {
        throw new Error('The SDS file could not be loaded.');
      }

      this.previewBlobUrl = URL.createObjectURL(file);
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.previewBlobUrl}#zoom=100`);
    } catch (_) {
      this.messageService.add({
        severity: 'error',
        summary: 'SDS',
        detail: this.translate.instant('SDS_PDF_NOT_AVAILABLE')
      });
      this.previewVisible = false;
    } finally {
      this.previewLoading = false;
    }
  }

  closeProductPreview(): void {
    this.previewVisible = false;
    this.previewLoading = false;
    this.revokePreviewBlob();
  }

  private revokePreviewBlob(): void {
    if (this.previewBlobUrl) {
      URL.revokeObjectURL(this.previewBlobUrl);
      this.previewBlobUrl = null;
    }
  }

  ngOnInit(): void {
    this.statusFilter = 'active';
    this.recentlyAssessed.clear();
    this.load();
  }

  onStatusChange(): void {
    // A different status view starts fresh — drop the "just assessed" highlight.
    this.recentlyAssessed.clear();
    this.load();
  }

  load(): void {
    if (!this.businessId) {
      return;
    }
    this.loading = true;
    this.selected = [];
    this.sdsApi.getSubstitutionAssessments(this.businessId, this.statusFilter).subscribe({
      next: rows => {
        this.rows = this.orderRows(rows);
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.loading = false;
        this.toast('error', 'SDS_SUBST_LOAD_FAILED');
      }
    });
  }

  applyGlobalFilter(value: string): void {
    this.assessTable?.filterGlobal(value, 'contains');
  }

  // ----- Assess -----

  assessSelected(): void {
    // Deleted chemicals are never assessable — drop them even if the
    // header "select all" checkbox swept them into the selection.
    const assessable = this.selected.filter(r => r.Status !== 'SoftDeleted');
    this.openAssess(assessable.map(r => r.DocumentId));
  }

  /** Opens the info popup showing the most recent assessment note for a row. */
  viewNote(row: SdsSubstitutionAssessmentRow): void {
    this.notePopupProduct = row.Product;
    this.notePopupText = row.LastNote || '';
    this.notePopupVisible = true;
  }

  assessOne(row: SdsSubstitutionAssessmentRow): void {
    if (row.Status === 'SoftDeleted') {
      return;
    }
    this.openAssess([row.DocumentId], row.Product);
  }

  private openAssess(docIds: number[], productName?: string): void {
    if (!docIds.length) {
      return;
    }
    this.pendingDocIds = docIds;
    this.assessmentDate = new Date();
    this.reminderDate = this.defaultReminder();
    this.assessNote = '';
    this.assessDialogVisible = true;

    // Pre-fill the note; the user can edit it. Single-row uses the product name,
    // a multi-select assessment uses the generic wording.
    //
    // get() rather than instant(): the note is persisted verbatim, so resolving it
    // before the active language bundle has loaded would silently store the English
    // fallback and leave a Norwegian user with an English note forever.
    const key = productName ? 'SDS_SUBST_NOTE_DEFAULT' : 'SDS_SUBST_NOTE_DEFAULT_GENERIC';
    this.translate
      .get(key, productName ? { product: productName } : undefined)
      .subscribe(text => {
        // Don't clobber anything the user has already typed.
        if (!this.assessNote) {
          this.assessNote = text;
        }
      });
  }

  get pendingCount(): number {
    return this.pendingDocIds.length;
  }

  confirmAssess(): void {
    if (!this.pendingDocIds.length || !this.assessmentDate) {
      return;
    }
    this.assessDialogSaving = true;
    this.sdsApi.assessSubstitution(this.businessId, {
      DocumentIds: this.pendingDocIds,
      AssessmentDate: this.toIso(this.assessmentDate),
      ReminderDate: this.reminderDate ? this.toIso(this.reminderDate) : null,
      Note: this.assessNote?.trim() || null
    }).subscribe({
      next: () => {
        this.assessDialogSaving = false;
        this.assessDialogVisible = false;
        // Remember what was just assessed so the refresh floats them to the top in green.
        this.pendingDocIds.forEach(id => this.recentlyAssessed.add(id));
        this.toast('success', 'SDS_SUBST_ASSESS_SUCCESS');
        this.load();
      },
      error: () => {
        this.assessDialogSaving = false;
        this.toast('error', 'SDS_SUBST_ASSESS_FAILED');
      }
    });
  }

  // ----- Replace -----

  /** Opens the two-option Replace modal: replace with existing, or create a new chemical. */
  replace(row: SdsSubstitutionAssessmentRow): void {
    this.replaceTarget = row;
    this.replaceStep = 'choice';
    this.selectedReplacementCandidate = null;
    this.replaceCandidateSuggestions = [];
    this.replaceReason = '';
    this.replaceError = null;
    this.replaceModalVisible = true;
  }

  /** Switches the modal to the "search and pick an existing chemical" step. */
  chooseReplaceExisting(): void {
    this.replaceStep = 'existing';
    this.replaceError = null;
  }

  /** Routes to the create wizard, pre-flagged so the new SDS links as the replacement. */
  chooseReplaceNew(): void {
    const target = this.replaceTarget;
    this.replaceModalVisible = false;
    if (target) {
      this.router.navigate(['../new'], {
        relativeTo: this.route,
        queryParams: { replaces: target.DocumentId }
      });
    }
  }

  /** Drives the replacement-candidate autocomplete; discards stale responses. */
  searchReplaceCandidates(event: { query: string }): void {
    if (!this.replaceTarget) {
      return;
    }
    const requestId = ++this.replacementSearchRequestId;
    this.sdsApi
      .getReplacementCandidates(this.businessId, this.replaceTarget.DocumentId, event?.query || '')
      .subscribe({
        next: results => {
          if (requestId === this.replacementSearchRequestId) {
            this.replaceCandidateSuggestions = results || [];
          }
        },
        error: () => {
          if (requestId === this.replacementSearchRequestId) {
            this.replaceCandidateSuggestions = [];
          }
        }
      });
  }

  /** Assigns the chosen existing chemical as the replacement (server validates cycles). */
  confirmReplaceExisting(): void {
    if (!this.replaceTarget || !this.selectedReplacementCandidate) {
      this.replaceError = this.translate.instant('SDS_REPLACEMENT_SELECT_REQUIRED');
      return;
    }

    this.replaceSaving = true;
    this.replaceError = null;

    this.sdsApi
      .assignReplacement(
        this.businessId,
        this.replaceTarget.DocumentId,
        this.selectedReplacementCandidate.DocumentId,
        this.replaceReason?.trim() || null
      )
      .subscribe({
        next: () => {
          this.replaceSaving = false;
          this.replaceModalVisible = false;
          this.replaceTarget = null;
          this.toast('success', 'SDS_SUBST_REPLACE_SUCCESS');
          this.load();
        },
        error: err => {
          this.replaceSaving = false;
          // Surfaces the backend's circular-reference / validation message.
          this.replaceError =
            err?.error?.errors?.ReplacementDocumentId?.[0] ||
            err?.error?.title ||
            err?.error?.detail ||
            this.translate.instant('SDS_REPLACEMENT_FAILED');
        }
      });
  }

  closeReplaceModal(): void {
    if (this.replaceSaving) {
      return;
    }
    this.replaceModalVisible = false;
    this.replaceTarget = null;
  }

  // ----- Helpers -----

  isAssessmentCurrent(row: SdsSubstitutionAssessmentRow): boolean {
    return !!row.LastAssessmentDate && !row.IsOverdue;
  }

  isRowAssessed(row: SdsSubstitutionAssessmentRow): boolean {
    return ( this.recentlyAssessed.has(row.DocumentId) || this.isAssessmentCurrent(row));
  }

  /** Assessed/current rows first, then overdue, then the rest — each group by product name. */
  private orderRows(rows: SdsSubstitutionAssessmentRow[]): SdsSubstitutionAssessmentRow[] {
    const rank = (r: SdsSubstitutionAssessmentRow): number => {
      if (this.recentlyAssessed.has(r.DocumentId)) {
        return 0;
      }

      if (this.isAssessmentCurrent(r)) {
        return 1;
      }

      return r.IsOverdue ? 2 : 3;
    };

    return [...rows].sort((a, b) => {
      const diff = rank(a) - rank(b);
      if (diff !== 0) {
        return diff;
      }

      const dateDiff = this.toTime(b.LastAssessmentDate) - this.toTime(a.LastAssessmentDate);
      return dateDiff !== 0 ? dateDiff : (a.Product || '').localeCompare(b.Product || '');
    });
  }

  private toTime(value?: string | null): number {
    if (!value) {
      return 0;
    }

    const time = new Date(value).getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  // ----- Export -----

  get canExport(): boolean {
    return !this.exportLoading && this.rows.length > 0;
  }

  openExportMenu(event: Event): void {
    this.exportMenuItems = [
      { label: 'Excel', icon: 'pi pi-file-excel', command: () => this.dispatchExport('XLSX') },
      { label: 'PDF', icon: 'pi pi-file-pdf', command: () => this.dispatchExport('PDF') },
      { label: 'CSV', icon: 'pi pi-file', command: () => this.dispatchExport('CSV') }
    ];
    this.exportMenu?.toggle(event);
  }

  private dispatchExport(format: SdsExportFormat): void {
    if (!this.canExport) {
      return;
    }

    const header = this.translate.instant('SDS_SUBST_TITLE');
    const { columnNames, data } = this.buildExportPayload();

    this.exportLoading = true;
    this.sdsExport.run(format, data, columnNames, header, 'substitution-assessment').subscribe({
      next: () => {
        this.exportLoading = false;
      },
      error: () => {
        this.exportLoading = false;
        this.toast('error', 'SDS_SUBST_EXPORT_FAILED');
      }
    });
  }

  /**
   * Flattens the grid into spreadsheet-ready rows. Values are rendered exactly as the
   * user sees them on screen — dates as dd/MM/yyyy rather than raw ISO timestamps, and
   * status/overdue as translated words rather than internal enum names.
   */
  private buildExportPayload(): { columnNames: SdsExportColumn[]; data: any[]; } {
    const columnNames: SdsExportColumn[] = [
      { title: this.translate.instant('SDS_PRODUCT_NAME'), dataKey: 'Product' },
      { title: 'CAS', dataKey: 'CASNumber' },
      { title: this.translate.instant('SDS_SUPPLIER'), dataKey: 'Supplier' },
      { title: this.translate.instant('SDS_SUBST_LAST_ASSESSED'), dataKey: 'LastAssessed' },
      { title: this.translate.instant('SDS_SUBST_REMINDER'), dataKey: 'Reminder' },
      { title: this.translate.instant('SDS_SUBST_STATUS'), dataKey: 'StatusLabel' },
      { title: this.translate.instant('SDS_SUBST_NOTE'), dataKey: 'Note' }
    ];

    // Export what the user is actually looking at, filters and sort included.
    const source = (this.assessTable?.filteredValue || this.rows) as SdsSubstitutionAssessmentRow[];

    const data = source.map(row => ({
      Product: row.Product || '',
      CASNumber: row.CASNumber || '',
      Supplier: row.Supplier || '',
      LastAssessed: row.LastAssessmentDate
        ? this.sdsExport.formatDate(row.LastAssessmentDate)
        : this.translate.instant('SDS_SUBST_NEVER'),
      Reminder: this.sdsExport.formatDate(row.ReminderDate),
      StatusLabel: this.translate.instant('SDS_SUBST_STATUS_' + (row.Status || '').toUpperCase()),
      Note: row.LastNote || ''
    }));

    return { columnNames, data };
  }

  private defaultReminder(): Date {
    const d = new Date();
    d.setMonth(d.getMonth() + 11);
    return d;
  }

  /** Local date as yyyy-MM-dd (avoids the UTC off-by-one a raw toISOString would cause). */
  private toIso(d: Date): string {
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  }

  private toast(severity: 'success' | 'error', key: string): void {
    this.messageService.add({
      severity,
      summary: this.translate.instant('SDS_SUBST_TITLE'),
      detail: this.translate.instant(key)
    });
  }
}
